import { Suspense, lazy, memo } from 'react';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { ModelCategoryV1 } from 'pages/ModelOverviewPage/blockv1/types';
import { Flags } from 'models/Global.interface';
import BlockList from 'pages/ModelOverviewPage/block';
import { isDimensionsRevampEnabled } from 'utils/dimensionsRevampFlag';
import { isDashboardRevampEnabled } from 'utils/dashboardRevampFlag';

import { TabType } from '../types';
import { HEADER_CONFIG } from '../constants';

// Lazy load heavy components
const Dashboard = lazy(() => import('pages/ModelOverviewPage/Dashboard-v2'));
// Selected by the LaunchDarkly `DashboardRevamp` flag.
const DashboardRevamp = lazy(() => import('pages/ModelOverviewPage/DashboardRevamp'));
const Dimensions = lazy(() => import('pages/ModelOverviewPage/dimensions'));
const DimensionsRevamp = lazy(() => import('pages/ModelOverviewPage/DimensionsRevamp'));
const DataImport = lazy(() => import('pages/ModelOverviewPage/Import'));
const BlockListV1: any = lazy(() => import('pages/ModelOverviewPage/blockv1'));

interface TabContentProperties {
  activeTab: TabType;
  blocks?: any[];
  categories?: ModelCategoryV1[];
  flags?: Flags;
}

const LoadingFallback = () => (
  <Center h='200px'>
    <Spinner size='xl' color='#745BE3' thickness='4px' />
  </Center>
);

const TabContent = memo(({ activeTab, blocks, categories, flags }: TabContentProperties) => {
  // const isTablet = Boolean((useMediaQuery('(max-width: 768px)') as any)[0]);

  return (
    <Box
      height={`calc(100vh - ${HEADER_CONFIG.HEIGHT})`}
      overflow='auto'
      // Dimensions stays on V1's own dark body gradient. It was switched to the
      // revamp's light gradient here, which put the new chrome behind the
      // original UI — whose text is white, so it read as invisible on the pale
      // background. The light treatment belongs to
      // ModalOverViewPageHeaderRevamp, not to V1.
      bg={
        (flags?.blocksManagement && activeTab === 'blocks') || activeTab === 'dashboard'
          ? 'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)'
          : 'gradient.body'
      }
      // Dimensions ran flush to all four edges because it was grouped with the
      // zero-padding tabs. 3 = 12px, enough to lift the grid off the edges
      // without reflowing it.
      px={
        // eslint-disable-next-line no-nested-ternary
        activeTab === 'dimensions'
          ? 3
          : (flags?.blocksManagement && activeTab === 'blocks') || activeTab === 'dashboard'
          ? 0
          : 4
      }
      pt={
        // eslint-disable-next-line no-nested-ternary
        activeTab === 'dimensions'
          ? 3
          : (flags?.blocksManagement && activeTab === 'blocks') || activeTab === 'dashboard'
          ? 0
          : 4
      }
      pb={activeTab === 'dimensions' ? 3 : 0}
    >
      {/* <Box px={isTablet ? 4 : 7} pt={3}>
        <BreadCrumbComponent hasBlock={false} />
      </Box> */}
      <Box
        height={`calc(100vh - ${
          !flags?.uiRevamp ? HEADER_CONFIG.HEIGHT : HEADER_CONFIG.HEIGHT_UI_REVAMP
        })`}
      >
        <Suspense fallback={<LoadingFallback />}>
          {activeTab === 'dashboard' &&
            (isDashboardRevampEnabled(flags) ? (
              <DashboardRevamp />
            ) : (
              // The original dashboard renders white text because it was built
              // for the app's dark `gradient.body`. This shell (Header.tsx)
              // paints a light gradient for the revamped UI, which left the
              // original's text invisible — so restore its own backdrop here
              // rather than editing the pristine Dashboard-v2 files.
              <Box bg='gradient.body' height='100%' overflow='auto'>
                <Dashboard />
              </Box>
            ))}
          {activeTab === 'blocks' &&
            (flags?.blocksManagement ? (
              <BlockListV1 blocks={blocks || []} categories={categories || []} />
            ) : (
              <BlockList blocks={blocks || []} />
            ))}
          {activeTab === 'dimensions' &&
            (isDimensionsRevampEnabled(flags) ? <DimensionsRevamp /> : <Dimensions />)}
          {activeTab === 'import' && <DataImport />}
        </Suspense>
      </Box>
    </Box>
  );
});

TabContent.displayName = 'TabContent';

export default withLDConsumer()(TabContent);
